import tagTascader from '../components/tag-cascader.vue'
import FileUploader from '@/components/FileUploader/index.vue'
import {
  statusesOptions,
  categoriesOptions,
  typesOptions,
  visibleOptions,
} from './label-config'
import type {
  TagListItem,
  TagGroupListItem,
  FileData,
  FileType,
} from '@/api/material_label/type'
import { v2Host } from '@/config/host'
import { getToken } from '@/utils/auth'
import { getApiPath } from '@/common/api-proxy.js'

const uploadConfig = {
  action: `${v2Host}${getApiPath('/upload/file')}`,
  headers: {
    'Auth-Token': getToken(),
  },
  drag: true,
  data: {
    mod: 'asset-tag-icon',
    bucket: 'mws-files',
  },
}

export const fromModel: TagListItem = {
  name: '',
  name_en: '',
  group_id: '',
  status: 2,
  description: '',
  icon_url: '',
  fileList: [],
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function fromSchema() {
  return {
    type: 'object',
    properties: {
      name: {
        component: 'input',
        title: '标签名称：',
        rules: [
          { required: true, message: '标签名称不能为空', trigger: 'blur' },
        ],
        field: {
          style: {
            width: '250px',
          },
        },
      },
      name_en: {
        component: 'input',
        title: '标签英文名：',
        rules: [
          { required: true, message: '标签英文不能为空', trigger: 'blur' },
        ],
        field: {
          placeholder: '请输入标签英文名',
          style: {
            width: '250px',
          },
        },
      },
      group_id: {
        title: '标签组：',
        component: tagTascader,
        treeParams: { group_type: 3 },
        rules: [
          {
            required: true,
            message: '标签组不能为空',
            trigger: 'change',
          },
        ],
      },
      status: {
        title: '标签状态：',
        component: 'radio-group',
        field: {
          options: statusesOptions,
        },
      },
      description: {
        title: '资源描述：',
        component: 'input',
        field: {
          type: 'textarea',
          placeholder: '请输入内容',
          style: {
            width: '450px',
          },
          maxlength: 30,
          'show-word-limit': true,
        },
      },
      icon_url: {
        title: '标签封面：',
        component: FileUploader,
        field: {
          ...uploadConfig,
          accept: '.jpg,.png',
          fileList: fromModel.fileList,
          onRemove: (): void => {
            fromModel.fileList = []
            fromModel.icon_url = ''
          },
          // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
          onChange: (file: FileType, fileList: FileType[]): void => {
            if (fileList.length > 1) {
              fileList.splice(0, 1)
            }
          },
          onSuccess: (res: FileData): void => {
            fromModel.icon_url = res.data.file_path || ''
            fromModel.fileList = [
              {
                name: res.data.origin_name,
                url: res.data.url,
              },
            ]
          },
          uploadText: '支持JPG、PNG格式，大小不超过2M',
        },
      },
    },
  }
}

export const fromGroupModel: TagGroupListItem = {
  pid: '',
  level: 0,
  name: '',
  name_en: '',
  category: 2,
  asset_type: '',
  visible: 1,
  is_leaf: 0,
  public: 0,
  required: 0,
  choice: 0,
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function fromGroupSchema(
  level: number,
  is_leaf: number,
  states: string,
) {
  return {
    type: 'object',
    properties: {
      pid: {
        title: '位置：',
        component: tagTascader,
        treeParams: { group_type: 1 },
        states: states,
        visible: level !== 0,
        rules: [
          {
            required: true,
            message: '位置不能为空',
            trigger: 'change',
          },
        ],
        field: {
          style: {
            width: '250px',
          },
        },
      },
      name: {
        title: '标签组名称：',
        component: 'input',
        rules: [
          { required: true, message: '标签组名称不能为空', trigger: 'blur' },
        ],
        field: {
          style: {
            width: '250px',
          },
        },
      },
      name_en: {
        title: '标签组英文名：',
        component: 'input',
        rules: [
          { required: true, message: '标签组英文名不能为空', trigger: 'blur' },
        ],
        field: {
          style: {
            width: '250px',
          },
        },
      },
      category: {
        title: '标签组分类：',
        component: 'select',
        rules: [{ required: true, message: '标签组分类：', trigger: 'change' }],
        visible: level === 0,
        field: {
          clearable: true,
          disabled: true,
          style: {
            width: '250px',
          },
          options: categoriesOptions,
        },
      },
      asset_type: {
        title: '元素类型：',
        component: 'select',
        rules: [
          {
            required: true,
            message: '元素类型不能为空',
            trigger: 'change',
          },
        ],
        visible: level === 0,
        field: {
          clearable: true,
          style: {
            width: '250px',
          },
          options: typesOptions,
        },
      },
      visible: {
        title: '标签组可见范围：',
        component: 'select',
        visible: level === 0,
        field: {
          clearable: true,
          disabled: true,
          style: {
            width: '250px',
          },
          options: visibleOptions,
        },
      },
      is_leaf: {
        title: '是否最末一层标签组：',
        component: 'radio-group',
        field: {
          options: [
            {
              value: 1,
              label: '是',
            },
            {
              value: 2,
              label: '否',
            },
          ],
        },
      },
      public: {
        title: '是否公开：',
        component: 'radio-group',
        visible: is_leaf !== 2,
        field: {
          options: [
            {
              value: 1,
              label: '是，公开',
            },
            {
              value: 2,
              label: '否，不公开',
            },
          ],
        },
      },
      required: {
        title: '是否必选：',
        component: 'radio-group',
        visible: is_leaf !== 2,
        field: {
          options: [
            {
              value: 1,
              label: '是，必选',
            },
            {
              value: 2,
              label: '否，非必选',
            },
          ],
        },
      },
      choice: {
        title: '单选多选：',
        component: 'radio-group',
        visible: is_leaf !== 2,
        field: {
          options: [
            {
              value: 1,
              label: '单选',
            },
            {
              value: 2,
              label: '多选',
            },
          ],
        },
      },
    },
  }
}
